import type {
  SearchCoffeesQuery,
  SearchCoffeesQueryVariables,
} from '@/api/generated/graphql';
import { MIN_QUERY_LENGTH, SEARCH_COFFEES_QUERY } from '@/api/queries/search';
import { useAuthedClient } from '@/api/useAuthedClient';
import type { SearchCoffeeResult } from '@/types/search';

import { useGraphQLQuery } from './useGraphQLQuery';

interface UseSearchCoffeesOptions {
  /**
   * Called with the trimmed query and its results after each successful fetch
   * for the *current* query — never for aborted or superseded-query
   * completions. Fires with the real result set, so `coffee_searched` can
   * record an accurate `result_count` instead of an in-flight empty one.
   */
  onResult?: (query: string, results: SearchCoffeeResult[]) => void;
}

interface UseSearchCoffeesReturn {
  results: SearchCoffeeResult[];
  isLoading: boolean;
  error: Error | null;
}

/** A response tagged with the query it was issued for. */
interface SearchResult {
  query: string;
  results: SearchCoffeeResult[];
}

export function useSearchCoffees(
  query: string,
  limit: number,
  offset: number,
  options: UseSearchCoffeesOptions = {},
): UseSearchCoffeesReturn {
  const { onResult } = options;
  const trimmed = query.trim();
  const authedClient = useAuthedClient();

  const { data, isLoading, error } = useGraphQLQuery<SearchResult>({
    fetcher: async (signal) => {
      const requestQuery = trimmed;
      const response = await authedClient.request<SearchCoffeesQuery, SearchCoffeesQueryVariables>({
        document: SEARCH_COFFEES_QUERY,
        variables: { query: requestQuery, limit, offset },
        signal,
      });
      return { query: requestQuery, results: response.searchCoffees };
    },
    deps: [trimmed, limit, offset],
    enabled: trimmed.length >= MIN_QUERY_LENGTH,
    initialLoading: false,
    onSuccess: (result) => {
      // A response can resolve in the window between a query-change render and
      // the effect that aborts its request; discard completions whose query has
      // been superseded so `onResult` only ever sees the current query.
      if (result.query !== trimmed) return;
      onResult?.(result.query, result.results);
    },
  });

  return {
    results: data?.results ?? [],
    isLoading,
    error,
  };
}
